// @givmo/social — the pages' shared parts: the page frame, a state card, the quiet loading line, a
// refused read in the words its refusal allows, and the date words every page uses.

import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { Button, Card, Icon, PageHeader, cn, testIdProp } from '@givmo/design-system';
import { isGivmoError } from '@givmo/api-client';
import { socialWords as w } from './social-words';

export function SocialPageShell({
  title,
  description,
  back,
  actions,
  testId,
  children,
  width = 'wide',
}: {
  title: ReactNode;
  description?: ReactNode;
  /** A way back to an ANCESTOR page: a router link, matched exactly (the shell's checklist). */
  back?: { to: string; label: string };
  actions?: ReactNode;
  testId: string;
  children: ReactNode;
  width?: 'wide' | 'narrow';
}) {
  // One left edge for every Social page: a narrow page is only narrower, never re-centred, so moving
  // from the list to a post doesn't shift the column under the person's eye.
  return (
    <div
      className={cn(
        'mx-auto flex w-full max-w-[1120px] flex-col gap-24 p-24 max-sm:p-16',
        width === 'narrow' && '[&>*]:max-w-[760px]',
      )}
      {...testIdProp(testId)}
    >
      {back ? (
        <Link
          to={back.to}
          activeOptions={{ exact: true }}
          className="-mb-12 inline-flex w-fit items-center gap-4 font-sans text-s13 font-w500 tracking-givmo text-text-secondary hover:text-text-strong"
          {...testIdProp('social-back')}
        >
          <Icon icon={ChevronLeft} size={16} aria-hidden />
          {back.label}
        </Link>
      ) : null}
      <PageHeader title={title} subtitle={description} actions={actions} />
      {children}
    </div>
  );
}

export function SocialStateCard({
  title,
  description,
  action,
  testId,
}: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  testId: string;
}) {
  return (
    <Card className="flex flex-col items-start gap-8 px-24 py-24" testId={testId}>
      <h2 className="font-sans text-s16 font-w600 tracking-givmo text-text-strong">{title}</h2>
      {description ? (
        <p className="max-w-[640px] font-sans text-s14 tracking-givmo text-text-secondary">
          {description}
        </p>
      ) : null}
      {action ? <div className="mt-8">{action}</div> : null}
    </Card>
  );
}

/** A quiet loading line, announced to a screen reader. */
export function SocialLoading({ children }: { children: string }) {
  return (
    <p
      role="status"
      className="font-sans text-s13 text-text-secondary"
      {...testIdProp('social-loading')}
    >
      {children}
    </p>
  );
}

/** A read that failed: the route miss in its own words (`gone`), anything else with a retry. */
export function SocialRefused({
  error,
  onRetry,
  gone,
}: {
  error: unknown;
  onRetry: () => void;
  gone?: { title: string; description: string };
}) {
  const missing = isGivmoError(error) && error.status === 404 && gone !== undefined;
  if (missing) {
    return (
      <SocialStateCard testId="social-gone" title={gone.title} description={gone.description} />
    );
  }
  return (
    <SocialStateCard
      testId="social-read-failed"
      title={w.readFailedTitle}
      description={w.readFailedBody}
      action={
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          onClick={onRetry}
          testId="social-retry-read"
        >
          {w.tryAgain}
        </Button>
      }
    />
  );
}

/** A moment in the person's own time: "Tue, Nov 18, 9:00 AM". */
/** A time later today as a clock reads it ("5:09 AM"); another day's, with its day. */
export function clockWords(iso: string | null | undefined): string {
  if (!iso) return '';
  const moment = new Date(iso);
  if (Number.isNaN(moment.getTime())) return '';
  if (moment.toDateString() !== new Date().toDateString()) return whenWords(iso);
  return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(moment);
}

/** …or on the clock of `zone` (an IANA name) where one is given: a renter's organization's. */
export function whenWords(iso: string | null | undefined, zone: string | null = null): string {
  if (!iso) return '';
  const moment = new Date(iso);
  if (Number.isNaN(moment.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(zone ? { timeZone: zone } : {}),
  }).format(moment);
}

/** The zone's everyday short name ("ET"), as a renter's own list names it; null where the browser
 *  has none for it. */
export function zoneShortName(zone: string): string | null {
  try {
    return (
      new Intl.DateTimeFormat(undefined, { timeZone: zone, timeZoneName: 'shortGeneric' })
        .formatToParts(new Date())
        .find((part) => part.type === 'timeZoneName')?.value ?? null
    );
  } catch {
    return null;
  }
}

/** A post's time as its view says it: on `zone`'s clock, named ("Fri, Nov 20, 9:00 AM ET"), where a
 *  renter gives its organization's zone, so it never disagrees with the renter's own list beside it;
 *  else as `whenWords` says it, on the person's own clock. */
export function namedWhenWords(iso: string | null | undefined, zone: string | null = null): string {
  const words = whenWords(iso, zone);
  if (!words || zone === null) return words;
  const short = zoneShortName(zone);
  return short ? `${words} ${short}` : words;
}

/** A day: "Nov 18, 2026". */
export function dayWords(iso: string | null | undefined): string {
  if (!iso) return '';
  const moment = new Date(iso);
  if (Number.isNaN(moment.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(moment);
}

/** The person's time zone as people say it ("Pacific Time"), or `zone`'s where one is given (a
 *  renter's organization's, "Eastern Time"), for the schedule's line: the generic name, which a
 *  daylight-saving change between now and the post's time doesn't make wrong; else the zone's id. */
export function timeZoneName(zone: string | null = null): string {
  try {
    const named = new Intl.DateTimeFormat(undefined, {
      timeZoneName: 'longGeneric',
      ...(zone ? { timeZone: zone } : {}),
    })
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')?.value;
    return named || zone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'your local time';
  } catch {
    return 'your local time';
  }
}
